/**
 * SocialProofPopup — the storefront "recent orders" social-proof toast.
 *
 * Mounted once inside <BrowserRouter>. Polls the public /social-proof/feed and,
 * when the admin has the feature enabled, slides a small "Jordan M. from
 * Austin, TX just ordered BPC-157" card up from the bottom-left, cycling through
 * recent orders. Purely cosmetic / marketing — never blocks the page.
 *
 * Design notes:
 *  - Uses plain fetch (not the axios client) so it stays fully decoupled from
 *    the auth interceptor — this runs for logged-out visitors too.
 *  - Hidden on /admin so it never overlays the dashboard.
 *  - Reads the live feed from a ref so a background refresh never interrupts the
 *    card that's currently on screen.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { productImage, onImageError, formatPrice } from '../utils/helpers';

const FEED_URL = (process.env.REACT_APP_API_URL || '/api') + '/social-proof/feed';
const POLL_MS    = 90000; // refresh the feed every 90s
const FIRST_MS   = 3500;  // wait before the first card so the page settles
const VISIBLE_MS = 6000;  // how long each card stays up
const GAP_MS     = 9000;  // quiet gap between cards

function timeAgo(iso) {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function SocialProofPopup() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  const eventsRef = useRef([]);
  const cursor = useRef(0);
  const [active, setActive] = useState(false);  // enabled & has events & storefront
  const [current, setCurrent] = useState(null);
  const [shown, setShown] = useState(false);

  // ── poll the public feed ──────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(FEED_URL, { credentials: 'include' })
        .then(r => r.json())
        .then(d => {
          if (!alive) return;
          eventsRef.current = Array.isArray(d.events) ? d.events : [];
          setActive(!!d.enabled && eventsRef.current.length > 0);
        })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ── rotate cards while active ─────────────────────────────────────────────
  useEffect(() => {
    if (isAdmin || !active) {
      setShown(false);
      return;
    }
    let stopped = false;
    const timers = [];

    const cycle = () => {
      if (stopped) return;
      const list = eventsRef.current;
      if (list.length === 0) return;
      const ev = list[cursor.current % list.length];
      cursor.current += 1;
      setCurrent(ev);
      setShown(true);
      timers.push(setTimeout(() => {
        setShown(false);
        timers.push(setTimeout(cycle, GAP_MS));
      }, VISIBLE_MS));
    };

    timers.push(setTimeout(cycle, FIRST_MS));
    return () => {
      stopped = true;
      timers.forEach(clearTimeout);
      setShown(false);
    };
  }, [active, isAdmin]);

  if (isAdmin || !current) return null;

  const place = [current.city, current.region].filter(Boolean).join(', ');

  return (
    <div
      aria-live="polite"
      className={
        'fixed bottom-4 left-4 z-40 max-w-[20rem] transition-all duration-500 ease-out ' +
        (shown ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0 pointer-events-none')
      }
    >
      <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl shadow-xl ring-1 ring-black/5 border border-gray-100 dark:border-gray-700 p-3 pr-9 relative">
        <img
          src={productImage(current.productSlug)}
          onError={onImageError}
          alt={current.productName}
          className="h-12 w-12 rounded-xl object-cover bg-gray-100 dark:bg-gray-700 shrink-0"
        />
        <div className="min-w-0">
          <div className="text-sm text-gray-800 dark:text-gray-100 leading-snug">
            <span className="font-semibold">{current.displayName}</span>
            {place && <span className="text-gray-500 dark:text-gray-400"> in {place}</span>}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 truncate">
            ordered <span className="font-medium text-tea-700 dark:text-tea-300">{current.productName}</span>
            {current.variantLabel && <span className="text-gray-400"> · {current.variantLabel}</span>}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-400">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
            Verified order
            <span aria-hidden>·</span>
            {timeAgo(current.occurredAt)}
            {current.amountInCents != null && (
              <>
                <span aria-hidden>·</span>
                <span className="text-gray-500">{formatPrice(current.amountInCents)}</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => setShown(false)}
          aria-label="Dismiss"
          className="absolute top-1.5 right-1.5 h-6 w-6 flex items-center justify-center rounded-full text-gray-300 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 text-lg leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
}
